import { Controller } from "@hotwired/stimulus"
import { initializeApp } from "firebase/app"
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging"

// Registers this browser for FCM web push and keeps the token in sync with the
// server. Permission must be requested from a user gesture, hence the button.
export default class extends Controller {
  static values = { config: Object, vapidKey: String }
  static targets = ["enable", "status"]

  async connect() {
    this.ready = false

    if (!(await this.supported())) {
      this.element.hidden = true
      return
    }

    this.messaging = getMessaging(initializeApp(this.configValue))
    this.ready = true

    // Foreground messages never reach the service worker, so render them here.
    onMessage(this.messaging, (payload) => this.showForeground(payload))

    // Already granted on a previous visit: refresh the token silently. FCM
    // rotates tokens, so this keeps the server copy current.
    if (Notification.permission === "granted") {
      this.subscribe()
    }

    this.render()
  }

  async enable() {
    if (!this.ready) return

    const permission = await Notification.requestPermission()
    this.render()

    if (permission === "granted") await this.subscribe()
  }

  async subscribe() {
    try {
      const registration = await navigator.serviceWorker.register("/service-worker.js", { scope: "/" })
      await navigator.serviceWorker.ready

      // Without an explicit registration the SDK looks for
      // /firebase-messaging-sw.js, which this app does not serve.
      const token = await getToken(this.messaging, {
        vapidKey: this.vapidKeyValue,
        serviceWorkerRegistration: registration
      })

      if (!token) return

      await fetch("/device_tokens", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ token })
      })
    } catch (error) {
      console.error("[push] subscription failed", error)
    }
  }

  showForeground(payload) {
    const data = payload.data || {}
    if (Notification.permission !== "granted") return

    const notification = new Notification(data.title || "Timeline", {
      body: data.body,
      icon: "/icon.png"
    })

    notification.onclick = () => {
      window.focus()
      if (data.path) window.location.href = data.path
    }
  }

  render() {
    if (!this.hasEnableTarget) return

    const granted = Notification.permission === "granted"
    this.enableTarget.hidden = granted

    if (this.hasStatusTarget) {
      this.statusTarget.hidden = !granted
    }
  }

  async supported() {
    if (!("Notification" in window) || !("serviceWorker" in navigator)) return false

    // iOS only allows web push from a home-screen installed PWA.
    return await isSupported()
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }
}
